import * as React from 'react'

import { PopoverTagPicker } from '~/client/src/shared/components/PopoverTagPicker/PopoverTagPicker'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import { Tag } from '~/client/src/shared/components/Tag/Tag'
import Localization from '~/client/src/shared/localization/LocalizationManager'

const MAX_VISIBLE_ROWS = 3

export interface IBulkListEditorRenderArgs<T> {
  /** The item being edited, or undefined when adding a new one. */
  initial: T | undefined
  /** The current staged list — for consumer-side validation (e.g. uniqueness). */
  items: T[]
  /** Index being edited, or null when adding (so validation can exclude self). */
  editIndex: number | null
  /** Append (when adding) or replace (when editing), then return to the list. */
  commit: (item: T) => void
  /**
   * Stage several items as one act, for an editor whose subject is not a single
   * item — a weekly schedule is authored as a week and replaces the week that was
   * there, not appended a day at a time. Merged by `applyItems`.
   */
  commitMany: (items: T[]) => void
  /** Close the editor without committing. */
  close: () => void
}

interface IProps<T> {
  label: string
  /** Shown in the field while the staged list is empty. */
  emptyPlaceholder: string
  /**
   * Seeds the staged list on mount. Omit (the default) to open empty; Date closures
   * is the one control that seeds, using the union of the selection's current
   * values. Mount-only — the parent reseeds by remounting this editor (keyed) on
   * open, so in-progress edits are never clobbered by a parent re-render.
   */
  initialItems?: T[]
  /** className for the editor popover (sizes it per control). */
  editorPopoverClassName?: string
  /** Disables Apply/Cancel while a bulk write is in flight (prevents double-submit). */
  isApplying?: boolean
  /**
   * Rendered before the staged chips — for a non-removable leading chip such as the
   * inherited-hours default. Supersedes the empty placeholder when it returns a
   * node, and is pinned in the picker's `leading` slot so it never collapses behind
   * "+n more". Receives the staged list so a control whose default is superseded by
   * what the user stages (e.g. hours of operation) can drop the chip.
   */
  renderLeading?: (items: T[]) => React.ReactNode
  /**
   * Overrides how a committed item is merged into the staged list. Default: append
   * when adding, replace at `editIndex` when editing. Lets a control enforce a
   * cardinality rule — hours of operation keeps a single daily window, so a saved
   * daily window replaces the staged one rather than adding a second.
   */
  applyItem?: (items: T[], item: T, editIndex: number | null) => T[]
  /**
   * How a `commitMany` set is merged into the staged list. Default: it replaces
   * the list. Hours of operation keeps any dated windows and swaps only the
   * recurring ones, the two recurrence modes being mutually exclusive.
   */
  applyItems?: (items: T[], next: T[]) => T[]
  /**
   * Overrides the staged chips when several items are one thing to the user — a
   * weekly schedule is one chip that opens the whole week and clears it, not a
   * chip per weekday. Receives the staged list and the same edit/replace handles
   * the default rendering uses.
   */
  /**
   * Must be a FLAT array of chips, one root DOM node each - never a fragment
   * wrapping several. PopoverTagPicker takes React.Children.toArray of what it
   * is given as its logical tag list while measuring the real DOM children, and
   * a fragment collapses to one logical child while emitting several DOM nodes,
   * so the "+n more" slice is computed against one list and applied to another.
   */
  renderChips?: (args: {
    items: T[]
    edit: (index: number | null) => void
    replace: (next: T[]) => void
  }) => React.ReactNode[]
  /**
   * Allow Apply with an empty list. For replace-style controls (e.g. hours of
   * operation) an empty list is meaningful — it clears per-resource overrides so
   * the selection falls back to the inherited project default. Default false, so
   * Apply stays disabled while the list is empty.
   */
  allowEmptyApply?: boolean
  renderLabel: (item: T) => React.ReactNode
  renderEditor: (args: IBulkListEditorRenderArgs<T>) => React.ReactNode
  onApply: (items: T[]) => void
  onCancel: () => void
}

/**
 * Bulk chip-list editor: stages a local draft list and applies it once. The
 * staged values render in a `PopoverTagPicker` field (same chip rendering and
 * "+n more" overflow the per-row cells use); the "+" / a chip-click opens the
 * caller-supplied editor as that picker's popover. Apply is gated on `isApplying`
 * and (unless `allowEmptyApply`) on a non-empty list, derived from the current
 * count (never latched), so removing the last chip re-disables it.
 */
function BulkListEditor<T>({
  label,
  emptyPlaceholder,
  editorPopoverClassName,
  isApplying = false,
  initialItems,
  renderLeading,
  applyItem,
  applyItems,
  renderChips,
  allowEmptyApply = false,
  renderLabel,
  renderEditor,
  onApply,
  onCancel,
}: IProps<T>) {
  const [items, setItems] = React.useState<T[]>(() => initialItems ?? [])
  // null = editor closed; -1 = adding; >= 0 = editing that index.
  const [editIndex, setEditIndex] = React.useState<number | null>(null)

  const commit = (item: T) => {
    const target = editIndex! < 0 ? null : editIndex
    setItems(prev => {
      if (applyItem) return applyItem(prev, item, target)
      return target == null
        ? [...prev, item]
        : prev.map((existing, i) => (i === target ? item : existing))
    })
    setEditIndex(null)
  }

  const commitMany = (next: T[]) => {
    setItems(prev => (applyItems ? applyItems(prev, next) : next))
    setEditIndex(null)
  }

  return (
    <div className="tw:flex tw:flex-col tw:gap-2.5">
      <span className="tw:text-body-md tw:font-medium tw:text-foreground">
        {label}
      </span>
      <PopoverTagPicker
        isOpen={editIndex !== null}
        onOpen={() => setEditIndex(-1)}
        onClose={() => setEditIndex(null)}
        placeholder={emptyPlaceholder}
        triggerLabel={label}
        variant="bordered"
        cellAffordance="plus"
        maxVisibleRows={MAX_VISIBLE_ROWS}
        popoverClassName={editorPopoverClassName}
        leading={renderLeading?.(items)}
        popoverContent={
          // Skip building the editor while closed; the `=== null` guard also
          // narrows editIndex to a number in the else-branch. Key by editIndex
          // so switching edit targets remounts the editor and reseeds its
          // mount-only state (mirrors the per-row cells' `key={editingIndex}`).
          editIndex === null ? null : (
            <React.Fragment key={editIndex}>
              {renderEditor({
                initial: editIndex >= 0 ? items[editIndex] : undefined,
                items,
                editIndex: editIndex >= 0 ? editIndex : null,
                commit,
                commitMany,
                close: () => setEditIndex(null),
              })}
            </React.Fragment>
          )
        }
      >
        {renderChips
          ? renderChips({ items, edit: setEditIndex, replace: setItems })
          : items.map((item, index) => (
              <Tag
                key={index}
                className="tw:cursor-pointer"
                onClick={event => {
                  event.stopPropagation()
                  setEditIndex(index)
                }}
                onDismiss={() =>
                  setItems(prev => prev.filter((_, i) => i !== index))
                }
              >
                {renderLabel(item)}
              </Tag>
            ))}
      </PopoverTagPicker>
      <div className="tw:flex tw:items-center tw:justify-end tw:gap-2">
        <Button variant="outline" onClick={onCancel} disabled={isApplying}>
          {Localization.translator.cancel}
        </Button>
        <Button
          onClick={() => onApply(items)}
          disabled={(!allowEmptyApply && items.length === 0) || isApplying}
        >
          {Localization.translator.apply}
        </Button>
      </div>
    </div>
  )
}

export default BulkListEditor
